import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';
import {
    MEASURE_NAMES,
    MEASURE_BY_SKILL_NAME,
    SKILL_NAMES_BY_APPLICATION,
    CS_MEASURES,
    DEC_MEASURES,
    ORF_MEASURE_SCORES,
    NECESSITY,
    BURST_AVAILABILITY,
} from '../../constants/assessments';
import { PERIOD, BM_PERIODS } from '../../constants/enrollment';
import { BURST } from '../../constants/applications';
import { D8_INTERPRETATIONS } from '../../constants/interpretations';

const PLACEMENT_TEST_MEASURE_BLACKLIST = [
    ...CS_MEASURES,
    MEASURE_NAMES.MAZE,
    MEASURE_NAMES.LNF,
    MEASURE_NAMES.WRF,
];

/**
 * Check is measure available for placement test calculation.
 * LNF, WRF and CS measures should be ignored.
 * Only available and required measures taken as placement test measures.
 * RW is always required if IW is required.
 * @param measureAvailabilityByMeasure - availability map
 * @param measure - measure
 * @returns {boolean} - flag
 */
export const isMeasureAvailableForPlacementTest = (measureAvailabilityByMeasure, measure) => {
    const availability = measureAvailabilityByMeasure[measure];
    return (
        !PLACEMENT_TEST_MEASURE_BLACKLIST.includes(measure) &&
        availability.available &&
        (availability.necessity === NECESSITY.REQUIRED ||
            (measure === MEASURE_NAMES.DEC_RW &&
                measureAvailabilityByMeasure[MEASURE_NAMES.DEC_IW].necessity ===
                    NECESSITY.REQUIRED))
    );
};

/**
 * Check is dec available according to orf results
 * @param orfStatus - processed orf results status
 * @returns {boolean} - flag
 */
const isDecMeasureAvailableBasedOnOrfResult = orfStatus =>
    orfStatus.orfFluencyInterp === D8_INTERPRETATIONS.WELL_BELOW_BENCHMARK ||
    orfStatus.orfFluencyInterp === D8_INTERPRETATIONS.BELOW_BENCHMARK ||
    orfStatus.orfAccuracyInterp === D8_INTERPRETATIONS.WELL_BELOW_BENCHMARK;

/**
 * Check is cs available according to orf results
 * @param orfStatus - processed orf results status
 * @returns {boolean} - flag
 */
const isCompMeasureAvailableBasedOnOrfResult = orfStatus =>
    (orfStatus.orfFluencyInterp === D8_INTERPRETATIONS.ABOVE_BENCHMARK ||
        orfStatus.orfFluencyInterp === D8_INTERPRETATIONS.AT_BENCHMARK) &&
    orfStatus.orfAccuracyInterp !== D8_INTERPRETATIONS.WELL_BELOW_BENCHMARK;

/**
 * DEC has strict order so this method results latest valid dec result according to that order
 * @param burstResults - list of all burst results
 * @returns {Object} dec result
 * {
 *     'isValid': boolean,
 *     'isOfficial': boolean,
 *     'studentId': string,
 *     'measureGenus': string,
 *     'period': string,
 *     'results': [
 *         {
 *             'wasDiscontinued': boolean,
 *             'measure': string,
 *             'probeId': string,
 *             'score': number,
 *         },
 *     ],
 *     'grade': string,
 *     'state': number,
 *     'overallScore': number,
 * }
 */
const getLatestDecodeResult = burstResults => {
    let latestDecodeResult = null;
    DEC_MEASURES.forEach(measure => {
        const decodeResult = burstResults.find(result => result.measureGenus === measure);
        if (decodeResult) {
            latestDecodeResult = decodeResult;
        }
    });
    return latestDecodeResult;
};

/**
 * Calculate necessity for dec according to latest dec result discontinued value.
 * If last results was discontinued current measure should not be permitted, required otherwise.
 * @param latestDecResult - result
 * @returns {string} necessity. `not_permitted` or `required`
 */
const getDecMeasureNecessity = latestDecResult => {
    const wasDiscontinued = get(latestDecResult, 'results[0].wasDiscontinued', false);
    return wasDiscontinued ? NECESSITY.NOT_PERMITTED : NECESSITY.REQUIRED;
};

/**
 * Calculate orf status according to results.
 * @param results - orf results
 * @param gatedMeasuresStatuses - object with gated measure statuses by period
 * @param period - current period
 * @param studentId - student id to get results for
 * @returns {Object} orf status
 *  {
 *      isOrfResultAvailable: boolean,
 *      orfFluencyInterp: string,
 *      orfAccuracyInterp: string
 *  }
 */
const getOrfDetailsForPeriod = (results, gatedMeasuresStatuses, period, studentId) => {
    const orfDetails = {
        isOrfResultAvailable: false,
    };
    const studentResults = get(results, `${period}.${studentId}`, []);
    const gatedMeasuresStatus = gatedMeasuresStatuses[period];
    const orfResult = studentResults.find(result => result.measure === MEASURE_NAMES.ORF);
    if (orfResult) {
        const fluencyResult = orfResult.measureScores[ORF_MEASURE_SCORES.FLUENCY];
        const accResult = orfResult.measureScores[ORF_MEASURE_SCORES.ACCURACY];
        orfDetails.isOrfResultAvailable = true;
        orfDetails.orfFluencyInterp = fluencyResult.interpretation;
        orfDetails.orfAccuracyInterp = accResult.interpretation;
    } else if (Object.keys(gatedMeasuresStatus).includes(ORF_MEASURE_SCORES.FLUENCY)) {
        const fluencyResult = gatedMeasuresStatus[ORF_MEASURE_SCORES.FLUENCY];
        const accResult = gatedMeasuresStatus[ORF_MEASURE_SCORES.ACCURACY];
        orfDetails.isOrfResultAvailable = true;
        orfDetails.orfFluencyInterp = fluencyResult.interpretation;
        orfDetails.orfAccuracyInterp = accResult.interpretation;
    }
    return orfDetails;
};

/**
 * Calculates burst student availability according to default availability rules, burst and dibels results
 * @param studentId - student id to tke results for
 * @param grade - student grade to take default availability for
 * @param gatedMeasuresStatuses - object with gated measure statuses by period
 * @param burstResults - burst results
 * @param dibelsResults - dibels results
 * @returns {Object} availability object per measure
 * {
 *     {measure}: {
 *                  available: boolean,
 *                  complete: boolean,
 *                  discontinued: boolean,
 *                  measure: string,
 *                  necessity: string,
 *                  complete: boolean,
 *                  discontinued: boolean,
 *     }
 * }
 */
export const calculateBurstAvailability = (
    studentId,
    grade,
    gatedMeasuresStatuses,
    burstResults,
    dibelsResults,
) => {
    const measureAvailability = cloneDeep(BURST_AVAILABILITY[grade]);
    BM_PERIODS.forEach(period => {
        const studentBurstResults = get(burstResults, `${period}.${studentId}`, []);
        const orfStatus = getOrfDetailsForPeriod(
            dibelsResults,
            gatedMeasuresStatuses,
            period,
            studentId,
        );
        SKILL_NAMES_BY_APPLICATION[BURST].forEach(skillName => {
            const measure = MEASURE_BY_SKILL_NAME[skillName];
            const availability = measureAvailability[period][measure];
            if (measure === MEASURE_NAMES.VOC) {
                const vocResult = studentBurstResults.find(
                    result => result.measureGenus === MEASURE_NAMES.VOC,
                );
                if (vocResult) {
                    availability.complete = true;
                    availability.discontinued = get(vocResult, 'results[0].wasDiscontinued', false);
                }
            }
            if (CS_MEASURES.includes(measure)) {
                let compPreviouslyRequired = false;
                if (period === PERIOD.EOY) {
                    compPreviouslyRequired =
                        measureAvailability[PERIOD.MOY][MEASURE_NAMES.CS_PASSAGE].necessity ===
                        NECESSITY.REQUIRED;
                }
                const csResult = studentBurstResults.find(
                    result => result.measureGenus === MEASURE_NAMES.CS,
                );
                if (csResult) {
                    const csProbeResult = csResult.results.find(
                        result => result.measure === measure,
                    );
                    if (csProbeResult) {
                        availability.complete = true;
                        availability.discontinued = csProbeResult.wasDiscontinued;
                    }
                }
                if (availability && availability.available) {
                    if (measure === MEASURE_NAMES.CS_PASSAGE) {
                        if (compPreviouslyRequired) {
                            availability.necessity = NECESSITY.REQUIRED;
                        } else if (orfStatus.isOrfResultAvailable) {
                            availability.necessity = isCompMeasureAvailableBasedOnOrfResult(
                                orfStatus,
                            )
                                ? NECESSITY.REQUIRED
                                : NECESSITY.NOT_PERMITTED;
                        }
                    }
                    if (measure === MEASURE_NAMES.CS_INFERENCE) {
                        if (csResult) {
                            const literalResult = csResult.results.find(
                                result => result.measure === MEASURE_NAMES.CS_LITERAL,
                            );
                            if (literalResult) {
                                availability.necessity = literalResult.wasDiscontinued
                                    ? NECESSITY.NOT_PERMITTED
                                    : NECESSITY.REQUIRED;
                            }
                        }
                    }
                }
            } else if (DEC_MEASURES.includes(measure)) {
                const latestDecResult = getLatestDecodeResult(studentBurstResults);
                if (availability && availability.available && orfStatus.isOrfResultAvailable) {
                    if (isDecMeasureAvailableBasedOnOrfResult(orfStatus)) {
                        const decResult = studentBurstResults.find(
                            result => result.measureGenus === measure,
                        );
                        if (decResult) {
                            availability.complete = true;
                            availability.discontinued = get(
                                decResult,
                                'results[0].wasDiscontinued',
                                false,
                            );
                        }
                        if (measure === MEASURE_NAMES.DEC_IW) {
                            availability.necessity = NECESSITY.REQUIRED;
                        } else if (latestDecResult) {
                            const measureIndex = DEC_MEASURES.indexOf(measure);
                            const resultMeasureIndex = DEC_MEASURES.indexOf(
                                latestDecResult.measureGenus,
                            );
                            if (resultMeasureIndex >= measureIndex) {
                                availability.necessity = NECESSITY.REQUIRED;
                            } else if (
                                latestDecResult.measureGenus === DEC_MEASURES[measureIndex - 1]
                            ) {
                                availability.necessity = getDecMeasureNecessity(latestDecResult);
                            }
                        }
                    } else {
                        availability.necessity = NECESSITY.NOT_PERMITTED;
                    }
                }
            }
        });
    });
    return measureAvailability;
};

export default calculateBurstAvailability;
